import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'zustand';
import { Download, Trash2 } from 'lucide-react';
import { settingsStore } from '../state/settings';
import type { Language, ThemePreference } from '../state/settings';
import type { UnitsPreference } from '../lib/units';
import { getApi } from '../api/endpoints';
import { toastStore } from '../state/toasts';
import { errorMessageKey } from './ErrorBox';
import { useMe } from '../shell/me';
import { Dialog } from '../ui/Dialog';

export function downloadJson(name: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function SettingsPage() {
  const { t } = useTranslation();
  const s = useStore(settingsStore);
  const me = useMe();
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');
  const fail = (err: unknown) =>
    toastStore.getState().push({ kind: 'error', message: t(errorMessageKey(err)) });
  const confirmWord = t('settings.deleteConfirmWord');

  return (
    <div className="page narrow-page">
      <h1>{t('settings.title')}</h1>
      <section className="settings-section">
        <h2>{t('settings.preferences')}</h2>
        <label className="field">
          <span>{t('settings.language')}</span>
          <select value={s.language} onChange={(e) => s.setLanguage(e.target.value as Language)}>
            <option value="en">English</option>
            <option value="de">Deutsch</option>
          </select>
        </label>
        <label className="field">
          <span>{t('settings.units')}</span>
          <select value={s.units} onChange={(e) => s.setUnits(e.target.value as UnitsPreference)}>
            <option value="auto">{t('settings.unitsAuto')}</option>
            <option value="metric">{t('settings.unitsMetric')}</option>
            <option value="imperial">{t('settings.unitsImperial')}</option>
          </select>
        </label>
        <label className="field">
          <span>{t('settings.theme')}</span>
          <select value={s.theme} onChange={(e) => s.setTheme(e.target.value as ThemePreference)}>
            <option value="system">{t('settings.themeSystem')}</option>
            <option value="light">{t('settings.themeLight')}</option>
            <option value="dark">{t('settings.themeDark')}</option>
          </select>
        </label>
      </section>
      <section className="settings-section">
        <h2>{t('settings.privacy')}</h2>
        {me.status === 'ready' && (
          <p className="muted">
            {t('settings.signedInAs', { name: me.me.name, email: me.me.email })}
          </p>
        )}
        <div className="button-row">
          <button
            type="button"
            className="btn"
            onClick={() =>
              getApi()
                .exportMe()
                .then((data) => downloadJson('stageplottter-account-export.json', data), fail)
            }
          >
            <Download size={16} /> {t('settings.export')}
          </button>
          <button type="button" className="btn btn-danger" onClick={() => setConfirming(true)}>
            <Trash2 size={16} /> {t('settings.deleteAccount')}
          </button>
        </div>
        <p className="muted small">{t('settings.deleteHint')}</p>
      </section>
      {confirming && (
        <Dialog title={t('settings.deleteAccount')} onClose={() => setConfirming(false)}>
          <h2>{t('settings.deleteAccount')}</h2>
          <p>{t('settings.deleteWarning')}</p>
          <label className="field">
            <span>{t('settings.deleteType', { word: confirmWord })}</span>
            <input data-autofocus value={typed} onChange={(e) => setTyped(e.target.value)} />
          </label>
          <div className="button-row">
            <button type="button" className="btn" onClick={() => setConfirming(false)}>
              {t('action.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={typed.trim() !== confirmWord}
              onClick={() =>
                getApi()
                  .deleteMe()
                  .then(() => window.location.assign('/'), fail)
              }
            >
              {t('settings.deleteForever')}
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
}
